import { type RestaurantDetails, type RestaurantOpeningHours } from "@/API";
import type { FC } from "react";
import Text from "../Text";
import {
  Clock3Icon,
  FileTextIcon,
  Image,
  SearchIcon,
  ShoppingCartIcon,
  StarIcon,
  TagIcon,
} from "lucide-react";
import Badge from "../Badge";
import dayjs from "dayjs";
import { Skeleton } from "../ui/skeleton";
import { useTranslation } from "react-i18next";
import { DAYS } from "@/lib/constants";

type Props = {
  restaurant?: RestaurantDetails;
  isLoading?: boolean;
};

const RestaurantMainInformation: FC<Props> = ({ isLoading, restaurant }) => {
  const { t } = useTranslation();

  if (isLoading)
    return (
      <>
        <div className="flex overflow-x-auto gap-[8px]">
          <Skeleton className="rounded-[12px] h-[104px] w-[112px]" />
          <Skeleton className="rounded-[12px] h-[104px] w-[112px]" />
          <Skeleton className="rounded-[12px] h-[104px] w-[112px]" />
          <Skeleton className="rounded-[12px] h-[104px] w-[112px]" />
          <Skeleton className="rounded-[12px] h-[104px] w-[112px]" />
          <Skeleton className="rounded-[12px] h-[104px] w-[112px]" />
          <Skeleton className="rounded-[12px] h-[104px] w-[112px]" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[16px]">
          <div className="flex flex-col bg-[white] rounded-[12px] py-[8px] px-[16px] gap-[16px]">
            <div className="flex items-center gap-[4px]">
              <Skeleton className="h-[32px] w-[32px] rounded-[12px]" />
              <Skeleton className="h-[12px] w-[70px]" />
            </div>
            <div className="flex gap-[4px]">
              <Skeleton className="h-[12px] w-[70px] rounded-[40px]" />
              <Skeleton className="h-[12px] w-[70px] rounded-[40px]" />
            </div>
          </div>
          <div className="flex flex-col bg-[white] rounded-[12px] py-[8px] px-[16px] gap-[16px]">
            <div className="flex items-center gap-[4px]">
              <Skeleton className="h-[32px] w-[32px] rounded-[12px]" />
              <Skeleton className="h-[12px] w-[70px]" />
            </div>
            <div className="flex gap-[4px]">
              <Skeleton className="h-[12px] w-[70px] rounded-[40px]" />
              <Skeleton className="h-[12px] w-[70px] rounded-[40px]" />
            </div>
          </div>
          <div className="flex flex-col bg-[white] rounded-[12px] py-[8px] px-[16px] gap-[16px]">
            <div className="flex items-center gap-[4px]">
              <Skeleton className="h-[32px] w-[32px] rounded-[12px]" />
              <Skeleton className="h-[12px] w-[70px]" />
            </div>
            <div className="flex gap-[4px]">
              <Skeleton className="h-[12px] w-[70px] rounded-[40px]" />
              <Skeleton className="h-[12px] w-[70px] rounded-[40px]" />
            </div>
          </div>
          <div className="flex flex-col bg-[white] rounded-[12px] py-[8px] px-[16px] gap-[16px]">
            <div className="flex items-center gap-[4px]">
              <Skeleton className="h-[32px] w-[32px] rounded-[12px]" />
              <Skeleton className="h-[12px] w-[70px]" />
            </div>
            <div className="flex gap-[4px]">
              <Skeleton className="h-[12px] w-[70px] rounded-[40px]" />
              <Skeleton className="h-[12px] w-[70px] rounded-[40px]" />
            </div>
          </div>
          <div className="flex flex-col bg-[white] rounded-[12px] py-[8px] px-[16px] gap-[16px]">
            <div className="flex items-center gap-[4px]">
              <Skeleton className="h-[32px] w-[32px] rounded-[12px]" />
              <Skeleton className="h-[12px] w-[70px]" />
            </div>
            <div className="flex gap-[4px]">
              <Skeleton className="h-[12px] w-[70px] rounded-[40px]" />
              <Skeleton className="h-[12px] w-[70px] rounded-[40px]" />
            </div>
          </div>
          <div className="flex flex-col bg-[white] rounded-[12px] py-[8px] px-[16px] gap-[16px]">
            <div className="flex items-center gap-[4px]">
              <Skeleton className="h-[32px] w-[32px] rounded-[12px]" />
              <Skeleton className="h-[12px] w-[70px]" />
            </div>
            <div className="flex gap-[4px]">
              <Skeleton className="h-[12px] w-[70px] rounded-[40px]" />
              <Skeleton className="h-[12px] w-[70px] rounded-[40px]" />
            </div>
          </div>
          <div className="flex flex-col bg-[white] rounded-[12px] py-[8px] px-[16px] gap-[16px]">
            <div className="flex items-center gap-[4px]">
              <Skeleton className="h-[32px] w-[32px] rounded-[12px]" />
              <Skeleton className="h-[12px] w-[70px]" />
            </div>
            <div className="flex gap-[4px]">
              <Skeleton className="h-[12px] w-[70px] rounded-[40px]" />
              <Skeleton className="h-[12px] w-[70px] rounded-[40px]" />
            </div>
          </div>
        </div>
      </>
    );

  if (!restaurant) return null;

  const {
    menuItems,
    rating,
    ratingCount,
    openingHours,
    categories,
    firstSeenAt,
    lastRefreshedAt,
    promotions,
  } = restaurant;

  const images: string[] = menuItems
    .filter((item) => item?.imageUrl && item.categoryName)
    .map((item) => item?.imageUrl) as string[];

  const numberItemsWithoutImage = menuItems.filter(
    (item) => !item?.imageUrl && item?.categoryName,
  ).length;
  const numberItemsWithoutDescription = menuItems.filter(
    (item) => !item?.description && item?.categoryName,
  ).length;
  const numberItemsInStock = menuItems.filter(
    (item) => !item?.isSoldOut && item?.categoryName,
  ).length;
  const numberItemsSoldOut = menuItems.filter(
    (item) => item?.isSoldOut && item.categoryName,
  ).length;

  let statusShifts = "N/A";
  let openingHoursOfToday: null | RestaurantOpeningHours[] = null;

  if (openingHours && openingHours.length > 0) {
    const findOpeningHours = openingHours
      .filter((it) => it !== null)
      .filter((it) => it.day === DAYS[dayjs().day()]);

    statusShifts = "closed";

    if (findOpeningHours && findOpeningHours.length > 0) {
      openingHoursOfToday = findOpeningHours;
      statusShifts = "shift";
    }
  }

  return (
    <div className="pt-[16px]">
      <Text fontSize={16} fontWeight={600} color="#172B4D">
        Médias
      </Text>
      <div className="flex overflow-x-auto gap-[8px] min-h-[104px] mt-[12px] mb-[24px]">
        {images.map((image, index) => (
          <img
            className="rounded-[12px] object-cover h-[104px] w-[112px]"
            src={image}
            key={`${image}-${index}`}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[16px]">
        <div className="flex flex-col bg-[white] rounded-[12px] py-[8px] px-[16px] gap-[16px]">
          <div className="flex items-center  gap-[4px]">
            <div className="h-[32px] w-[32px] bg-[#F7F8F9] rounded-[12px] flex items-center justify-center">
              <StarIcon height={16} width={16} color="#758195" />
            </div>
            <Text fontWeight={600} fontSize={14} className="truncate">
              {t("ratings_and_reviews")}
            </Text>
          </div>
          <div className="flex gap-[4px]">
            <Badge>
              <StarIcon height={16} width={16} color="#758195" />
              <Text fontWeight={400} fontSize={14}>
                {rating ?? "N/A"}
              </Text>
            </Badge>
            <Badge>
              <Text fontWeight={400} fontSize={14}>
                {ratingCount ? `${ratingCount} Avis` : "N/A"}
              </Text>
            </Badge>
          </div>
        </div>

        <div className="flex flex-col bg-[white] rounded-[12px] py-[8px] px-[16px] gap-[16px]">
          <div className="flex items-center gap-[4px]">
            <div className="h-[32px] w-[32px] bg-[#F7F8F9] rounded-[12px] flex items-center justify-center">
              <Clock3Icon height={16} width={16} color="#758195" />
            </div>
            <Text fontWeight={600} fontSize={14}>
              {t("opening_hours")}
            </Text>
          </div>
          <div className="flex gap-[4px]">
            {statusShifts === "N/A"
              ? "N.C"
              : statusShifts === "closed"
                ? t("closed")
                : openingHoursOfToday &&
                  openingHoursOfToday.map((openingHours) => (
                    <Badge
                      key={`${openingHours.menuName}-${openingHours.startTime}-${openingHours.endTime}`}
                    >
                      <Text fontWeight={400} fontSize={14}>
                        {openingHours
                          ? `${openingHours.startTime} - ${openingHours.endTime}`
                          : "N/A"}
                      </Text>
                    </Badge>
                  ))}
          </div>
        </div>

        <div className="flex flex-col bg-[white] rounded-[12px] py-[8px] px-[16px] gap-[16px]">
          <div className="flex items-center gap-[4px]">
            <div className="h-[32px] w-[32px] bg-[#F7F8F9] rounded-[12px] flex items-center justify-center">
              <Image height={16} width={16} color="#758195" />
            </div>
            <Text fontWeight={600} fontSize={14}>
              {t("missing_content")}
            </Text>
          </div>
          <div className="flex flex-col gap-[8px]">
            <Badge>
              <Text fontWeight={400} fontSize={14}>
                {`${numberItemsWithoutImage} image(s) manquante(s)`}
              </Text>
            </Badge>
            <Badge>
              <Text fontWeight={400} fontSize={14}>
                {`${numberItemsWithoutDescription} description(s) manquante(s)`}
              </Text>
            </Badge>
          </div>
        </div>

        <div className="flex flex-col bg-[white] rounded-[12px] py-[8px] px-[16px] gap-[16px]">
          <div className="flex items-center gap-[4px]">
            <div className="h-[32px] w-[32px] bg-[#F7F8F9] rounded-[12px] flex items-center justify-center">
              <TagIcon height={16} width={16} color="#758195" />
            </div>
            <Text fontWeight={600} fontSize={14}>
              {t("food_type")}
            </Text>
          </div>
          <div className="flex gap-[8px] flex-wrap">
            {categories?.map((category) => (
              <Badge key={`category-${category}`}>
                <Text fontWeight={400} fontSize={14} color="#1D7AFC">
                  {category}
                </Text>
              </Badge>
            ))}
          </div>
        </div>

        <div className="flex flex-col bg-[white] rounded-[12px] py-[8px] px-[16px] gap-[16px]">
          <div className="flex items-center gap-[4px]">
            <div className="h-[32px] w-[32px] bg-[#F7F8F9] rounded-[12px] flex items-center justify-center">
              <SearchIcon height={16} width={16} color="#758195" />
            </div>
            <Text fontWeight={600} fontSize={14}>
              Découverte
            </Text>
          </div>
          <div className="flex gap-[8px]">
            <Badge>
              <Text fontWeight={400} fontSize={14}>
                {firstSeenAt ? dayjs(firstSeenAt).format("DD/MM/YYYY") : "N/A"}
              </Text>
            </Badge>
          </div>
        </div>

        <div className="flex flex-col bg-[white] rounded-[12px] py-[8px] px-[16px] gap-[16px]">
          <div className="flex items-center gap-[4px]">
            <div className="h-[32px] w-[32px] bg-[#F7F8F9] rounded-[12px] flex items-center justify-center">
              <FileTextIcon height={16} width={16} color="#758195" />
            </div>
            <Text fontWeight={600} fontSize={14}>
              {t("last_update")}
            </Text>
          </div>
          <div className="flex gap-[8px]">
            <Badge>
              <Text fontWeight={400} fontSize={14}>
                {lastRefreshedAt
                  ? dayjs(lastRefreshedAt).format("DD/MM/YYYY")
                  : "N.C"}
              </Text>
            </Badge>
          </div>
        </div>

        <div className="flex flex-col bg-[white] rounded-[12px] py-[8px] px-[16px] gap-[16px]">
          <div className="flex items-center gap-[4px]">
            <div className="h-[32px] w-[32px] bg-[#F7F8F9] rounded-[12px] flex items-center justify-center">
              <ShoppingCartIcon height={16} width={16} color="#758195" />
            </div>
            <Text fontWeight={600} fontSize={14}>
              {t("menu_items")}
            </Text>
          </div>
          <div className="flex gap-[8px]">
            <Badge>
              <Text fontWeight={400} fontSize={14}>
                {`${numberItemsInStock} en stock (${(numberItemsInStock / (numberItemsInStock + numberItemsSoldOut)) * 100}%)`}
              </Text>
            </Badge>
          </div>
        </div>

        <div className="flex flex-col bg-[white] rounded-[12px] py-[8px] px-[16px] gap-[16px]">
          <div className="flex items-center gap-[4px]">
            <div className="h-[32px] w-[32px] bg-[#F7F8F9] rounded-[12px] flex items-center justify-center">
              <ShoppingCartIcon height={16} width={16} color="#758195" />
            </div>
            <Text fontWeight={600} fontSize={14}>
              {t("promotions")}
            </Text>
          </div>
          <div className="flex gap-[8px]">
            <Badge>
              <Text fontWeight={400} fontSize={14}>
                {t("active_promotions", { count: promotions?.length ?? 0 })}
              </Text>
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantMainInformation;
